import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";

// Keep role literals aligned with the DB enum 'app_role'
export type AppRole = "admin" | "teacher";

interface UseUserRolesResult {
  roles: AppRole[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  hasAnyRole: (required: AppRole[] | ReadonlyArray<AppRole>) => boolean;
}

export function useUserRoles(): UseUserRolesResult {
  const { user } = useAuth();
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRoles = useCallback(async () => {
    if (!user) {
      setRoles([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    console.log("[useUserRoles] Fetching roles for user:", user.id);

    try {
      // Cast to any to avoid TS issues if generated types are not yet updated
      const { data, error } = await (supabase as any)
        .from("user_roles")
        .select("role")
        .eq("user_id", user.id);

      if (error) {
        console.error("[useUserRoles] Error fetching roles:", error);
        setError(error.message || "Roolien hakeminen epäonnistui");
        setRoles([]);
      } else {
        const fetched = (data || []).map((r: { role: AppRole }) => r.role) as AppRole[];
        console.log("[useUserRoles] Roles loaded:", fetched);
        setRoles(fetched);
      }
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const hasRole = useCallback((role: AppRole) => roles.includes(role), [roles]);

  const hasAnyRole = useCallback(
    (required: AppRole[] | ReadonlyArray<AppRole>) => required.some((r) => roles.includes(r)),
    [roles]
  );

  return {
    roles,
    loading,
    error,
    refresh: fetchRoles,
    hasRole,
    hasAnyRole,
  };
}
